import { useQuery, useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Card, Form, Input, InputNumber, Select, Space, Typography } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import { get, post } from '../../../api/client';
import type { LevelRow } from '../types';

const UNIT_OPTIONS = [
  { value: 'day', label: '天' },
  { value: 'month', label: '个月' },
];

/** 与后端 ListSubscriptionsQueryDto 的 membership 值域逐字一致（@IsIn(['active','expired','none'])） */
const MEMBERSHIP_OPTIONS = [
  { value: 'active', label: '会员在期内' },
  { value: 'expired', label: '会员已过期' },
  { value: 'none', label: '从未有过会员' },
];

const FILTER_LABELS: Record<string, string> = {
  keyword: '关键词',
  levelCode: '当前等级',
  membership: '有效期状态',
};

function describe(values: Record<string, any>): string {
  const parts: string[] = [];
  for (const key of ['keyword', 'levelCode', 'membership']) {
    if (values[key]) parts.push(`${FILTER_LABELS[key]}＝${values[key]}`);
  }
  return parts.join('、');
}

/**
 * 批量赠送：按条件给一批用户发一段会员时长，逐人一行留痕。
 *
 * 两条硬闸门在后端（POST /admin/membership/grants/batch）：一个筛选条件都不给即拒
 * （不提供条件＝给全站注册用户赠送会员），命中超过 1000 人即拒（要求缩小范围，而不是默默做一半）。
 * 这里只负责把条件说清楚并在点下去之前复述一遍——赠送落库后没有撤回通路。
 */
export function BatchGrantPanel() {
  const { message, modal } = App.useApp();
  const [form] = Form.useForm();

  const { data: levelData } = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => get<LevelRow[]>('/admin/membership/levels'),
  });
  const levels = levelData ?? [];

  const run = useMutation({
    mutationFn: (values: Record<string, unknown>) => post('/admin/membership/grants/batch', values),
    onSuccess: (r) => {
      const data = (r ?? {}) as { matched?: number; granted?: number };
      message.success(`命中 ${data.matched ?? 0} 人，实际发放 ${data.granted ?? 0} 人`);
      form.resetFields();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '批量赠送失败'),
  });

  const submit = async () => {
    let values: Record<string, any>;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const unitText = `${values.value}${values.unit === 'month' ? '个月' : '天'}`;
    modal.confirm({
      title: '确认批量赠送会员',
      width: 480,
      content: (
        <div>
          <p style={{ marginTop: 0 }}>
            人群条件：{describe(values) || '（未给条件，后端会直接拒绝）'}
          </p>
          <p>每人发放：{unitText}付费档会员</p>
          {values.reason ? <p>说明：{values.reason}</p> : null}
          <p style={{ marginBottom: 0 }}>
            仍在有效期内的用户从原到期日往后叠，不会截断已买到的时长。赠送一旦落库没有撤回通路。
          </p>
        </div>
      ),
      okText: '确认发放',
      onOk: () =>
        run.mutateAsync({
          keyword: values.keyword?.trim() || undefined,
          levelCode: values.levelCode || undefined,
          membership: values.membership || undefined,
          unit: values.unit,
          value: values.value,
          reason: values.reason?.trim() || undefined,
        }),
    });
  };

  return (
    <Card title="批量赠送" style={{ maxWidth: 720 }}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        用于活动补偿、故障安抚这类一次性发放。三个人群条件至少要给一个，一次最多 1000 人。
      </Typography.Paragraph>
      <Alert
        type="warning"
        showIcon
        icon={<SafetyCertificateOutlined />}
        message="发放即时生效、不可撤回"
        description="每个人都会留下一行发放记录（记在「发放记录」里，操作人是你），到期时间按叠加规则往后推。"
        style={{ marginBottom: 20 }}
      />
      <Form form={form} layout="vertical" style={{ maxWidth: 560 }}>
        <Form.Item name="keyword" label="关键词（用户名 / 姓名）">
          <Input allowClear placeholder="如某个手机号段或某批用户的姓名前缀" />
        </Form.Item>
        <Space style={{ display: 'flex' }} align="start" size={12}>
          <Form.Item name="levelCode" label="按当前等级" style={{ width: 200 }}>
            <Select
              allowClear
              placeholder="不限等级"
              options={levels.map((l) => ({ value: l.code, label: `${l.name}（${l.code}）` }))}
            />
          </Form.Item>
          <Form.Item name="membership" label="按会员有效期" style={{ width: 200 }}>
            <Select allowClear placeholder="不限状态" options={MEMBERSHIP_OPTIONS} />
          </Form.Item>
        </Space>
        <Space style={{ display: 'flex' }} align="start" size={12}>
          <Form.Item
            name="unit"
            label="赠送时长单位"
            rules={[{ required: true, message: '请选择单位' }]}
            style={{ width: 160 }}
          >
            <Select options={UNIT_OPTIONS} placeholder="天 / 个月" />
          </Form.Item>
          <Form.Item
            name="value"
            label="赠送时长"
            rules={[{ required: true, message: '请填写赠送时长' }]}
            style={{ width: 160 }}
          >
            <InputNumber min={1} max={120} style={{ width: '100%' }} placeholder="如 7" />
          </Form.Item>
        </Space>
        <Form.Item
          name="reason"
          label="赠送说明"
          tooltip="会原样进入每一行发放记录，写清为什么送，日后对得上"
        >
          <Input maxLength={200} placeholder="如 10 月支付故障补偿" />
        </Form.Item>
        <Button type="primary" loading={run.isPending} onClick={submit}>
          批量赠送
        </Button>
      </Form>
    </Card>
  );
}
